import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WhyWorkWithUsSlider } from '@/components/WhyWorkWithUsSlider';
import { Helmet } from 'react-helmet-async';

export default function OurAttorneys({ onOpenPopup }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const attorneys = [
    {
      name: 'Dominique James',
      bio: 'An active California attorney admitted on May 8, 2025, and a Yale Law School graduate, practicing with Latham & Watkins in Los Angeles. Focuses on corporate law, mergers and acquisitions, & business advisory matters.',
      barId: '360839',
      location: 'Los Angeles, CA',
    },
    {
      name: 'Devin S. Crowley',
      bio: 'An active California attorney admitted to the State Bar on December 2, 2014. Focuses on civil litigation, business law, legal counseling, dispute resolution, and client advocacy, assisting individuals and organizations with a wide range of legal matters.',
      barId: '300577',
      location: 'San Francisco, CA',
    },
    {
      name: 'Joseph Lee',
      bio: 'An experienced attorney focused on corporate law, commercial transactions, business advisory, contract negotiations, and strategic legal guidance, helping businesses navigate complex legal and regulatory matters.',
      barId: '249435',
      location: 'New York, NY',
    },
    {
      name: 'Scott R James',
      bio: 'An accomplished attorney specializing in business law, commercial litigation, dispute resolution, risk management, and legal counsel, providing practical solutions for businesses and individual clients.',
      barId: '294378',
      location: 'Chicago, IL',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="our-attorneys-page">
      <Helmet>
        <title>Our Attorneys - Trademark Law Consulting | Expert Legal Team</title>
        <meta name="description" content="Meet our experienced team of trademark attorneys. Skilled legal professionals dedicated to protecting your brand and business interests across the United States." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/8112131/pexels-photo-8112131.jpeg"
            alt="Hero background"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-[#0A1C3E] opacity-90" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm font-medium tracking-widest uppercase text-[#E1A334] mb-4 block">
              Meet The Team
            </span>
            <h1 className="text-5xl md:text-6xl font-heading font-bold text-white leading-tight mb-6">
              Our{' '}
              <span className="text-[#E1A334]">
                Attorneys
              </span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto">
              Dedicated legal professionals committed to protecting your brand and business interests with expertise and integrity.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Attorneys Section - Different Layout */}
      <section className="py-12 md:py-20 bg-white" data-testid="attorneys-section">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {attorneys.map((attorney, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.02, y: -5 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative bg-white rounded-3xl p-8 shadow-lg border-2 border-slate-100 hover:border-[#E1A334] hover:shadow-2xl hover:shadow-[#E1A334]/20 transition-all duration-300"
                data-testid={`attorney-card-${index}`}
              >
                <div className="flex items-start gap-6 mb-6">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#0A1C3E] to-[#1B2F5C] flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl font-bold text-white">
                      {attorney.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-2xl text-slate-900 mb-1">
                      {attorney.name}
                    </h3>
                    <p className="text-[#E1A334] text-sm font-medium flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" />
                      {attorney.location}
                    </p>
                  </div>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed mb-6">
                  {attorney.bio}
                </p>
                <div className="border-t border-slate-100 pt-4">
                  <p className="text-slate-700 text-sm font-medium">
                    Bar ID: <span className="text-slate-500">{attorney.barId}</span>
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Work With Us Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-4">
              Why Choose Us
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Fast processing with most applications submitted within 24–48 hours after receiving complete details.
            </p>
          </motion.div>

          <WhyWorkWithUsSlider />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-[#0A1C3E] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-white/10" />
        </div>
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6">
              Work With Our Expert Team
            </h2>
            <p className="text-[#E1A334]/80 text-lg md:text-xl max-w-2xl mx-auto mb-8">
              Connect with our experienced attorneys to protect your brand. Start your trademark registration today.
            </p>
            <Button
              onClick={onOpenPopup}
              data-testid="cta-section-button"
              className="bg-white text-[#0A1C3E] hover:bg-[#E1A334] hover:text-white font-semibold rounded-full px-8 py-6 text-lg shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              Start Your Application Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
